import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Store } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StoreLocate } from "./StoreLocate";
import { StoreResults } from "./StoreResults";

// Section « Mes magasins » de la liste de courses — tous niveaux, aucun prix/stock
export function StoreFinder() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const cfg = useQuery({ queryKey: ["stores-config"], queryFn: async () => (await api.get("/stores/config")).data });
  const prefs = useQuery({ queryKey: ["store-prefs"], queryFn: async () => (await api.get("/stores/preferences")).data });

  const save = useMutation({
    mutationFn: async (zone) => (await api.put("/stores/preferences", zone)).data,
    onSuccess: (data) => { qc.setQueryData(["store-prefs"], data); toast.success(t("st_saved")); },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete("/stores/preferences")).data,
    onSuccess: (data) => { qc.setQueryData(["store-prefs"], data); setEditing(false); toast.success(t("st_deleted")); },
  });

  const zone = prefs.data?.store_zone;
  const config = cfg.data;

  return (
    <section className="chenia-card p-5 sm:p-6 mt-6" data-testid="store-finder">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary text-primary shrink-0"><Store className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h2 className="font-serif text-xl font-semibold tracking-tight">{t("st_title")}</h2>
          <p className="text-sm text-muted-foreground">{t("st_sub")}</p>
        </div>
      </div>

      {(cfg.isLoading || prefs.isLoading) && <p className="mt-4 text-sm text-muted-foreground">{t("loading")}</p>}

      {config && !config.configured && (
        <div className="mt-4 rounded-2xl bg-chenia-rosesoft/60 p-4 text-sm" data-testid="store-not-configured">{t("st_not_configured")}</div>
      )}

      {config && config.configured && (!zone || editing) && (
        <StoreLocate
          config={config}
          onConfirm={(cand) => {
            save.mutate({ label: cand.label, lat: cand.lat, lng: cand.lng, country: cand.country, country_detected: cand.country_detected,
              precision: cand.precision, mode: zone?.mode || "drive", radius_km: zone?.radius_km || config.default_radius_km, preferred: null });
            setEditing(false);
          }}
          onCancel={zone ? () => setEditing(false) : null}
        />
      )}

      {config && config.configured && zone && !editing && (
        <StoreResults
          zone={zone} config={config}
          onChange={(patch) => save.mutate({ ...zone, ...patch })}
          onEdit={() => setEditing(true)}
          onDelete={() => remove.mutate()}
        />
      )}

      <p className="mt-4 text-[11px] text-muted-foreground" data-testid="store-no-commercial">{t("st_no_commercial")}</p>
    </section>
  );
}
